import { forwardRef, useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { AlertCircle } from 'lucide-react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  icon?: ReactNode;
  hint?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, icon, hint, className = '', onFocus, onBlur, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(e);
    };

    return (
      <div className="space-y-1.5">
        {label && (
          <label className="block text-sm font-medium text-white/70 ml-1">
            {label}
          </label>
        )}
        <div className="relative">
          {/* Focus glow effect */}
          <AnimatePresence>
            {isFocused && !error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute -inset-0.5 bg-gradient-to-r from-accent-blue/30 to-accent-purple/30 rounded-[18px] blur-sm"
              />
            )}
          </AnimatePresence>

          {/* Error glow effect */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute -inset-0.5 bg-red-500/20 rounded-[18px] blur-sm"
              />
            )}
          </AnimatePresence>

          <div className="relative">
            {icon && (
              <div
                className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors duration-200 ${
                  isFocused ? 'text-accent-blue' : error ? 'text-red-400' : 'text-white/40'
                }`}
              >
                {icon}
              </div>
            )}
            <input
              ref={ref}
              onFocus={handleFocus}
              onBlur={handleBlur}
              className={`
                w-full glass rounded-2xl px-4 py-3.5 min-h-[52px]
                ${icon ? 'pl-12' : ''}
                text-white placeholder:text-white/30
                outline-none border border-transparent
                transition-all duration-200
                ${isFocused && !error ? 'border-accent-blue/50 bg-white/[0.08]' : ''}
                ${error ? 'border-red-500/50 bg-red-500/5' : ''}
                ${!isFocused && !error ? 'hover:bg-white/[0.06]' : ''}
                ${className}
              `}
              {...props}
            />

            {/* Error icon */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-red-400"
                >
                  <AlertCircle size={18} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Error or hint message */}
        <AnimatePresence mode="wait">
          {error ? (
            <motion.p
              key="error"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="text-sm text-red-400 ml-1 flex items-center gap-1.5"
            >
              {error}
            </motion.p>
          ) : hint ? (
            <motion.p
              key="hint"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-xs text-white/40 ml-1"
            >
              {hint}
            </motion.p>
          ) : null}
        </AnimatePresence>
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
